import React, { useMemo } from 'react'
import X from "../../icons/X.svg";
import { MainTracker } from '../../PixelTrackers/MainTracker';
import { translate } from '../../utils/translator';

type props = {
    responseData: ByteplantApiResponse | null
    language: string | undefined
    handleCustomerUsingSuggestion: (byteplantSuggestion: ByteplantApiResponse) => void
    handleCustomerIgnoreSuggestion: () => void
    onClose: () => void
    isExpress?: boolean
};


const ByteplantAddressValidatorModal: React.FC<props> = ({responseData, language, handleCustomerUsingSuggestion, handleCustomerIgnoreSuggestion, onClose, isExpress}) => {

    const statusCode = useMemo(() => {
        return responseData?.status || ""
    }, [responseData])

    const formattedAddress = useMemo(() => {
        return responseData?.formattedaddress || ""
    }, [responseData])

    const hasSuggestion = useMemo(() => {
        return Boolean(formattedAddress)
    }, [formattedAddress])

    const translationKeys = useMemo(() => {

        if (hasSuggestion) {
            MainTracker.track('pv', 'View Pop Up', {'Pop Up Name': translate('en', "default", "AddressValidationModal/title-suspected")})
            return {
                title: "AddressValidationModal/title-suspected",
                text: "AddressValidationModal/text-suspected",
                buttonChange: "AddressValidationModal/button-change-suspected",
                buttonKeep: "AddressValidationModal/button-keep-suspected",
            }
        } else {
            MainTracker.track('pv', 'View Pop Up', {'Pop Up Name': translate('en', "default", "AddressValidationModal/title-invalid")})
            return {
                title: "AddressValidationModal/title-invalid",
                text: "AddressValidationModal/text-invalid",
                buttonChange: "AddressValidationModal/button-change-invalid",
                buttonKeep: "AddressValidationModal/button-keep-invalid",
            }
        }

    },[language, hasSuggestion])


    if (!statusCode || !translationKeys) {
        return <></>
    }

    const buttonClasses = `cursor-pointer w-full md:w-[270px] hover:opacity-80 leading-[1] px-[40px] bg-white text-[#269C6E] border border-solid border-[#269C6E] rounded-[2.5px] md:rounded-[3px] font-bold transition-opacity duration-200 whitespace-nowrap flex justify-center items-center outline-none shadow-none ring-0 appearance-none ${hasSuggestion ? 'h-[44px] py-[10px] text-[14px]' : 'h-[43px] text-[16px]'} max-md:max-w-[335px]`;

    return (
        <div
            id="byteplant-address-validator-modal-container"
            className="flex flex-col relative bg-white rounded-tl-[30px] rounded-tr-[6px] rounded-bl-[6px] rounded-br-[30px] w-full max-w-[620px] border border-[#269C6E] pt-[30px] px-5 pb-[17px] md:px-[30px] md:pb-3 box-border overflow-x-hidden shadow-none"
        >

            {/* Modal Close button */}
            <div className='absolute top-6 right-6'>
                <img src={X} alt="X" className='w-6 h-6 cursor-pointer opacity-40 hover:opacity-70' onClick={onClose} />
            </div>

            {/* Modal Header */}
            <div className={`flex flex-col pr-8 ${hasSuggestion ? 'gap-[15px]' : 'gap-3 mb-6 md:mb-[19px]'}`}>
                <h1 className={`text-[24px] leading-[150%] font-bold text-[#0D2C54] font-[Poppins] m-0 break-words ${hasSuggestion ? 'flex-1 max-md:max-w-[263px]' : ''}`}>
                    {(() => {
                        const title = translate(language, "default", translationKeys.title);
                        const match = title.match(/^([^!]+!)(.*)$/);
                        if (match) {
                            return (
                                <>
                                    <span>{match[1]} </span>
                                    <span className="hidden md:inline"><br /></span>
                                    <span>{match[2].trim()}</span>
                                </>
                            );
                        }
                        return title;
                    })()}
                </h1>
                <p className={`text-[#656D79] text-[14px] leading-[150%] font-normal font-[Roboto] m-0 break-words ${hasSuggestion ? 'md:w-[430px] max-md:max-w-[294px]' : 'max-w-[436px]'}`}>
                    {translate(language, "default", translationKeys.text)} 
                </p>
            </div>


            {/* Address suggestion (only shown when suggestion exists) */}
            {hasSuggestion && (
                <div className='inline-flex items-center rounded-[6px] bg-[#F8F8F8] mt-[19px] mb-5 max-md:min-h-[62px] max-md:h-auto max-md:w-full max-md:px-[15px] max-md:py-[15px] md:h-[53px] md:px-[18px] md:py-[18px] md:pb-[19px] box-border'>
                    <p className='text-[#656D79] text-[16px] leading-[1.5] font-semibold m-0 whitespace-normal break-normal'>
                        {formattedAddress}
                    </p>
                </div>
            )}

            {/* Modal Action Buttons */}
            <div className={`w-full flex flex-col md:flex-row ${hasSuggestion ? 'gap-[10px] max-md:gap-[15px] mb-[15px]' : 'gap-[15px] md:gap-5 mb-[15px] md:mb-6'}`}>

                {/* Change Button (Take suggestion) */}
                <button 
                    type='button'
                    className={buttonClasses}
                    onClick={() => {
                        MainTracker.track('custom', `Decision on ${statusCode.toLowerCase()} address`, {'Answer': translate('en', "default", translationKeys.buttonChange)}) 
                        handleCustomerUsingSuggestion(responseData!);
                    }}>

                    {translate(language, "default", translationKeys.buttonChange)}
                </button>

                {/* Keep Button (Don't take suggestion). Not possible for Express delivery! */}
                {!isExpress &&
                    <button 
                        type='button'
                        className={buttonClasses}
                        onClick={() => {
                            MainTracker.track('custom', `Decision on ${statusCode.toLowerCase()} address`, {'Answer': translate('en', "default", translationKeys.buttonKeep)}) 
                            handleCustomerIgnoreSuggestion();
                        }}>
                            
                        {translate(language, "default", translationKeys.buttonKeep)}
                    </button>
                }

            </div>

            {/* Disclaimer */}
            <p className={`text-[#656D79] text-[12px] leading-[150%] font-normal font-[Roboto] m-0 break-words ${hasSuggestion ? 'md:w-[554px] max-md:max-w-[294px]' : 'max-w-[554px]'}`}>
                * {translate(language, "default", "AddressValidationModal/disclaimer")}
            </p>

        </div>
    );
}

export default ByteplantAddressValidatorModal
